import { useTranslation } from 'react-i18next'
import {
  Button,
  FormErrorMessage,
  Input,
  Menu,
  MenuGroup,
  MenuItem,
  Modal,
  ModalContent,
  ModalFooter,
  ModalProps,
  ModalTitle,
  Plus,
  formatHookFormErrors,
} from '@dashboard/ui'
import { useEffect, useState } from 'react'
import { MathExpressionParser } from '@/expr/math-expr/parser'
import { useDebounce } from '@/hooks'
import { AttrType, CampaignEventType, CustomAttribute, MathExpression, Token, TokenType } from '@/api/dashboard'
import { FieldGroup, Floating } from '@/ui'
import { Controller, useForm } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { Attribute, CAMPAIGN_ATTRIBUTES, PLAYER_ATTRIBUTES } from '@/expr/types'
import { useGameSettingsQuery } from '@/api/useGameSettingsQuery'
import { useGenerateCompanyPath } from '@/libs'
import AutocompleteTextarea from './components/AutocompleteTextarea'

export interface EditMathExprModalProps extends Omit<ModalProps, 'onChange'> {
  value?: MathExpression
  onChange: (expression: MathExpression) => void
  attributes?: Record<string, string>
  campaignType?: CampaignEventType | string
}

interface MathExpressionForm extends MathExpression {
  expression: string
}
interface VariableSelectMenuProps {
  onClose?: () => void
}

function checkAndUpdateTokens(
  tokens: Token[],
  playerCustomAttributes: CustomAttribute[],
  attributes: Attribute[],
  eventAttributes: Attribute[],
) {
  for (const token of tokens) {
    if (token.type != TokenType.Variable) {
      continue
    }

    const attr = playerCustomAttributes.find(attr => attr.name === token.value)
    if (attr) {
      token.type = TokenType.CustomAttribute
      continue
    }

    const attribute = attributes.find(attr => attr.name === token.value)
    if (attribute) {
      token.value = attribute.id
      token.type = TokenType.Attribute
      continue
    }

    const eventAttribute = eventAttributes.find(attr => attr.name === token.value)
    if (eventAttribute) {
      token.value = eventAttribute.id
      token.type = TokenType.EventAttribute
      continue
    }

    throw new Error(`Invalid token name: ${token.value}`)
  }
}

export const EditMathExprModal = (props: EditMathExprModalProps) => {
  const { t } = useTranslation()
  const { companyId, gameId } = useGenerateCompanyPath()
  const { data: gameSettings } = useGameSettingsQuery(companyId, gameId)

  const attributes = PLAYER_ATTRIBUTES.filter(attr => attr.type == AttrType.Number)
  const eventAttributes = props.campaignType
    ? CAMPAIGN_ATTRIBUTES[props.campaignType]?.filter(attr => attr.type == AttrType.Number) || []
    : []

  const {
    formState: { isSubmitting, errors },
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    control,
    setValue,
  } = useForm<MathExpressionForm>({
    defaultValues: {
      expression: props.value ? MathExpressionParser.stringify(props.value, attributes, eventAttributes) : '',
      name: props.value?.name || '',
      tokens: props.value?.tokens || [],
    },
  })

  const debouncedValue = useDebounce(watch('expression'), 300)
  const [caretPosition, setCaretPosition] = useState(0)

  useEffect(() => {
    if (!debouncedValue) {
      return
    }
    const parseResult = MathExpressionParser.parse(debouncedValue)
    if (parseResult.success) {
      clearErrors('expression')
    } else {
      setError('expression', {
        type: 'manual',
        message: parseResult.error || t('validation.invalid-expression'),
      })
    }
  }, [debouncedValue])

  const onSubmit = (data: MathExpressionForm) => {
    const parseResult = MathExpressionParser.parse(data.expression)

    if (parseResult.success && parseResult.expression) {
      const playerCustomAttributes = gameSettings?.player_attributes?.filter(attr => attr.type == AttrType.Number) || []

      try {
        checkAndUpdateTokens(parseResult.expression.tokens || [], playerCustomAttributes, attributes, eventAttributes)
      } catch (error) {
        setError('expression', {
          type: 'manual',
          message: error instanceof Error ? error.message : t('campaign.expression.invalid-expression'),
        })
        return
      }

      parseResult.expression.name = data.name
      props.onChange(parseResult.expression)
      props.onClose?.()
    }
  }

  const onItemSelected = (variable: string) => {
    const currentExpression = watch('expression')
    const beforeCaret = currentExpression.slice(0, caretPosition)
    const afterCaret = currentExpression.slice(caretPosition)
    const newExpression = `${beforeCaret}${variable}${afterCaret}`
    setValue('expression', newExpression, { shouldDirty: true })
  }

  const VariableSelectMenu = ({ onClose = () => void 0 }: VariableSelectMenuProps) => {
    const onClickVariable = (variable: string) => {
      onClose()
      onItemSelected(variable)
    }

    const playerAttributes = PLAYER_ATTRIBUTES.filter(it => it.type == AttrType.Number).sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    )

    const eventAttributesSorted = eventAttributes
      .filter(it => it.type == AttrType.Number)
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

    const customAttributes = (gameSettings?.player_attributes || [])
      .filter(it => it.type == AttrType.Number)
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

    return (
      <Menu>
        <MenuGroup label={t('sidebar.players.attributes')}> </MenuGroup>
        {playerAttributes.map(it => (
          <MenuItem
            onClick={e => {
              onClickVariable(it.name)
              e.stopPropagation()
              e.preventDefault()
            }}
            key={it.name}
          >
            {it.name}
          </MenuItem>
        ))}

        {eventAttributesSorted.length > 0 && <MenuGroup label={t('campaign.custom-event.attributes')}> </MenuGroup>}

        {eventAttributesSorted.map(it => (
          <MenuItem
            onClick={e => {
              onClickVariable(it.name)
              e.stopPropagation()
              e.preventDefault()
            }}
            key={it.name}
          >
            {it.name}
          </MenuItem>
        ))}

        {customAttributes.length > 0 && <MenuGroup label={t('player.custom-attributes')}> </MenuGroup>}

        {customAttributes.map(it => (
          <MenuItem
            onClick={e => {
              onClickVariable(it.name)
              e.stopPropagation()
              e.preventDefault()
            }}
            key={it.name}
          >
            {it.name}
          </MenuItem>
        ))}
      </Menu>
    )
  }

  return (
    <Modal onResize={() => {}} onClose={props.onClose} size="lg" deep={1}>
      <ModalTitle>Expression</ModalTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalContent>
          <FieldGroup label={t('name')}>
            <Input
              autoFocus
              className="w-full"
              {...register('name', {
                required: t('validation.required'),
                pattern: { value: /^[a-zA-Z][a-zA-Z0-9]*$/, message: t('campaign.expression.var-name') },
              })}
              {...formatHookFormErrors(errors, 'name')}
            />
          </FieldGroup>
          <FieldGroup
            label={t('campaign.attr-menu.expression')}
            extraRight={
              <Floating menu={<VariableSelectMenu />} referenceWidth={true}>
                <div className="ml-auto flex cursor-pointer items-center gap-1 text-caption-sm font-medium text-text-brand-primary hover:text-text-brand-solid">
                  <Plus size={11} />
                  {t('campaign.insert-var')}
                </div>
              </Floating>
            }
          >
            <Controller
              control={control}
              name={'expression'}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <AutocompleteTextarea
                  value={field.value}
                  onChange={v => field.onChange(v)}
                  placeholder="Use control option + space to open variable menu"
                  renderOverlay={({ close }) => <VariableSelectMenu onClose={close} />}
                  onCarePositionChange={v => setCaretPosition(v)}
                />
              )}
            />
            <div className="h-8">
              <ErrorMessage
                name="expression"
                errors={errors}
                render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
              />
            </div>
          </FieldGroup>
        </ModalContent>

        <ModalFooter>
          <Button variant="outline" onClick={props.onClose}>
            {t('Cancel')}
          </Button>
          <Button variant="default" type="submit" disabled={isSubmitting}>
            {t('Apply')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
